import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  cleanLoandiskUniqueNumber,
  collectionCaseKey,
  loandiskUniqueNumberKey,
} from '../utils/loandiskId';

interface CollectionEntry {
  id: string;
  account_balance: number;
  loandisk_id: string;
  status: string;
  created_at: string;
  profile_path: string;
  assigned_agent: {
    username: string;
    avatar_url?: string;
  } | null;
  borrower: {
    id: string;
    name: string;
    mobile_no: string;
    address: string;
  };
}

interface RawBorrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
}

interface RawAgent {
  username: string;
  avatar_url?: string;
}

interface RawCollectionEntry {
  id: string;
  account_balance: number | null;
  loandisk_id: string;
  status: string;
  created_at: string;
  assigned_agent: RawAgent[] | RawAgent;
  borrower: RawBorrower[] | RawBorrower;
}

interface RawLoandiskAssignment {
  id: string;
  loandisk_borrower_id: string;
  loandisk_unique_number: string | null;
  status: string;
  created_at: string;
  assigned_agent: RawAgent[] | RawAgent | null;
}

interface RawLoandiskBorrower {
  loandisk_borrower_id: string;
  unique_number: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  mobile: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
}

// Update the StyledTableCell to match CollectionDashboard style
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0.5),
    fontSize: '0.8rem'
  }
}));

const CollectionAgencyList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [entries, setEntries] = useState<CollectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 25 : 100); // Less rows per page on mobile
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [agents, setAgents] = useState<{id: string, username: string}[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balancesLoaded, setBalancesLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Transform raw data to CollectionEntry format
  const transformRawData = useCallback((rawData: RawCollectionEntry[]): CollectionEntry[] => {
    return rawData.map(item => {
      const borrower = Array.isArray(item.borrower)
        ? item.borrower[0]
        : item.borrower;
      const assignedAgent = item.assigned_agent
        ? (Array.isArray(item.assigned_agent)
            ? item.assigned_agent[0]
            : item.assigned_agent)
        : null;
      
      return {
        id: item.id,
        account_balance: Number(item.account_balance) || 0,
        loandisk_id: cleanLoandiskUniqueNumber(item.loandisk_id),
        status: item.status,
        created_at: item.created_at,
        profile_path: `/borrower/${item.id}`,
        assigned_agent: assignedAgent
          ? { username: assignedAgent.username, avatar_url: assignedAgent.avatar_url }
          : null,
        borrower: {
          id: borrower.id,
          name: borrower.name,
          mobile_no: borrower.mobile_no,
          address: borrower.address,
        },
      };
    });
  }, []);

  const fetchLoandiskAssignments = useCallback(async (): Promise<CollectionEntry[]> => {
    const { data: assignmentData, error: assignmentError } = await supabase
      .from('loandisk_collection_assignments')
      .select(`
        id,
        loandisk_borrower_id,
        loandisk_unique_number,
        status,
        created_at,
        agent_id,
        assigned_agent:profiles!agent_id (
          username,
          avatar_url
        )
      `)
      .order('created_at', { ascending: true })
      .limit(10000);

    if (assignmentError) throw assignmentError;
    const assignments = (assignmentData || []) as RawLoandiskAssignment[];
    if (assignments.length === 0) return [];

    const borrowerIds = Array.from(new Set(
      assignments.map((item) => item.loandisk_borrower_id).filter(Boolean)
    ));
    const borrowers: RawLoandiskBorrower[] = [];
    const batchSize = 100;

    for (let index = 0; index < borrowerIds.length; index += batchSize) {
      const { data, error } = await supabase
        .from('loandisk_borrowers')
        .select(
          'loandisk_borrower_id, unique_number, full_name, first_name, last_name, mobile, address, city, province'
        )
        .in('loandisk_borrower_id', borrowerIds.slice(index, index + batchSize));
      if (error) throw error;
      borrowers.push(...((data || []) as RawLoandiskBorrower[]));
    }

    const borrowersById = new Map(
      borrowers.map((borrower) => [borrower.loandisk_borrower_id, borrower])
    );

    return assignments.flatMap((assignment) => {
      const borrower = borrowersById.get(assignment.loandisk_borrower_id);
      if (!borrower) return [];

      const uniqueNumber = cleanLoandiskUniqueNumber(
        assignment.loandisk_unique_number || borrower.unique_number
      );
      if (!uniqueNumber) return [];

      const assignedAgent = assignment.assigned_agent
        ? (Array.isArray(assignment.assigned_agent)
          ? assignment.assigned_agent[0]
          : assignment.assigned_agent)
        : null;
      const name = borrower.full_name
        || [borrower.first_name, borrower.last_name].filter(Boolean).join(' ')
        || uniqueNumber;

      return [{
        id: assignment.id,
        account_balance: 0,
        loandisk_id: uniqueNumber,
        status: assignment.status,
        created_at: assignment.created_at,
        profile_path: `/loandisk-borrower/${encodeURIComponent(uniqueNumber)}`,
        assigned_agent: assignedAgent
          ? { username: assignedAgent.username, avatar_url: assignedAgent.avatar_url }
          : null,
        borrower: {
          id: assignment.loandisk_borrower_id,
          name,
          mobile_no: borrower.mobile || '',
          address: [borrower.address, borrower.city, borrower.province]
            .filter(Boolean)
            .join(', '),
        },
      }];
    });
  }, []);

  const fetchAndUpdateBalances = useCallback(async (entries: CollectionEntry[]) => {
    try {
      setLoadingBalances(true);
      
      // Get unique loandisk_ids
      const loandiskIds = Array.from(new Set(
        entries.map((item) => cleanLoandiskUniqueNumber(item.loandisk_id)).filter(Boolean)
      ));
      
      // Initialize balance map
      const balanceMap: { [key: string]: number } = {};
      
      // Process in batches
      const BATCH_SIZE = 30; // Slightly larger batch size for better performance
      
      for (let i = 0; i < loandiskIds.length; i += BATCH_SIZE) {
        const batchIds = loandiskIds.slice(i, i + BATCH_SIZE);
        
        try {
          const { data: loanData, error: loanError } = await supabase
            .from('loandisk_loans')
            .select('unique_number, loan_balance')
            .in('unique_number', batchIds);
          
          if (loanError) {
            console.error(`Error fetching loan data for batch:`, loanError);
            continue; 
          }
          
          // Calculate balances from this batch
          if (loanData && loanData.length > 0) {
            loanData.forEach(loan => {
              const id = loandiskUniqueNumberKey(loan.unique_number);
              const balance = Number(loan.loan_balance) || 0;
              
              if (!balanceMap[id]) {
                balanceMap[id] = 0;
              }
              
              balanceMap[id] += balance;
            });
          }
        } catch (err) {
          console.error(`Error processing loan batch:`, err);
        }
      }
      
      // Update entries with calculated balances
      const updatedEntries = entries.map(entry => ({
        ...entry,
        account_balance: balanceMap[loandiskUniqueNumberKey(entry.loandisk_id)] !== undefined
          ? balanceMap[loandiskUniqueNumberKey(entry.loandisk_id)]
          : entry.account_balance
      }));
      
      setEntries(updatedEntries);
      setBalancesLoaded(true);
    } catch (err) {
      console.error('Error updating balances:', err);
    } finally {
      setLoadingBalances(false);
      setIsFiltering(false); // Make sure to reset filtering state
    }
  }, []);

  const fetchCollectionEntries = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Starting to fetch collection entries...');

      // Get total count first for pagination info
      const { count, error: countError } = await supabase
        .from('for_collection')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        console.error('Error fetching count:', countError);
        throw countError;
      }

      setTotalCount(count || 0);
      console.log(`Total records found: ${count}`);

      // Use pagination to get ALL records (beyond 1000 limit)
      let allEntries: RawCollectionEntry[] = [];
      let lastId: string | null = null;
      let hasMore = true;
      let batchCount = 0;
      
      const batchSize = 1000; // Supabase's max limit per request
      
      while (hasMore && batchCount < 10) { // Safety limit of 10 batches (10,000 records)
        batchCount++;
        console.log(`Fetching batch ${batchCount}...`);
        
        let query = supabase
          .from('for_collection')
          .select(`
            id,
            account_balance,
            loandisk_id,
            status,
            created_at,
            agent_id,
            borrower:borrowers!inner (
              id,
              name,
              mobile_no,
              address
            ),
            assigned_agent:profiles!agent_id (
              username,
              avatar_url
            )
          `)
          .order('id', { ascending: true })
          .limit(batchSize);
        
        // For subsequent queries, start after the last ID we've seen
        if (lastId) {
          query = query.gt('id', lastId);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error(`Error fetching batch ${batchCount}:`, error);
          throw error;
        }
        
        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          const rawBatch = data as RawCollectionEntry[];
          allEntries = [...allEntries, ...rawBatch];
          lastId = rawBatch[rawBatch.length - 1].id;
          hasMore = data.length === batchSize;
          console.log(`Retrieved ${rawBatch.length} records in batch ${batchCount}. Total so far: ${allEntries.length}`);
        }
      }

      // Transform all the data 
      const transformedData = transformRawData(allEntries);
      const assignmentEntries = await fetchLoandiskAssignments();

      // One row per canonical LoanDisk unique number. Prefer the existing rich
      // collection profile, while taking assignment state from LoanDisk.
      const mergedEntries = new Map<string, CollectionEntry>();
      transformedData.forEach((entry) => {
        const key = collectionCaseKey(entry.id, entry.loandisk_id);
        const current = mergedEntries.get(key);
        if (
          !current
          || new Date(entry.created_at).getTime() > new Date(current.created_at).getTime()
        ) {
          mergedEntries.set(key, entry);
        }
      });
      assignmentEntries.forEach((entry) => {
        const key = collectionCaseKey(entry.id, entry.loandisk_id);
        const legacyEntry = mergedEntries.get(key);
        mergedEntries.set(
          key,
          legacyEntry
            ? {
                ...legacyEntry,
                status: entry.status,
                assigned_agent: entry.assigned_agent,
              }
            : entry
        );
      });
      const collectionEntries = Array.from(mergedEntries.values()).sort(
        (left, right) =>
          new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
      );
      
      // Update the UI with initial data
      setEntries(collectionEntries);
      setTotalCount(collectionEntries.length);
      setLoading(false);
      
      // Now fetch the balances separately
      await fetchAndUpdateBalances(collectionEntries);
      
    } catch (err) {
      console.error('Error fetching collection entries:', err);
      setLoading(false);
    }
  }, [transformRawData, fetchAndUpdateBalances, fetchLoandiskAssignments]);

  // New function to fetch a specific borrower if not in the current list
  const fetchBorrowerCollectionEntry = useCallback(async (borrowerId: string) => {
    try {
      setIsFiltering(true);
      
      // Check if the borrower is already in our loaded entries
      const existingEntry = entries.find(entry => entry.borrower.id === borrowerId);
      if (existingEntry) {
        console.log('Borrower already in loaded entries');
        return;
      }
      
      console.log(`Fetching specific borrower: ${borrowerId}`);
      
      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          account_balance,
            loandisk_id,
            status,
            created_at,
            agent_id,
          borrower:borrowers!inner (
            id,
            name,
            mobile_no,
            address
          ),
          assigned_agent:profiles!agent_id (
            username,
            avatar_url
          )
        `)
        .eq('borrower_id', borrowerId);
      
      if (error) {
        console.error('Error fetching borrower collection entry:', error);
        return;
      }
      
      if (data && data.length > 0) {
        const rawData = data as RawCollectionEntry[];
        const transformedData = transformRawData(rawData);
        
        // Add the new entries to our existing list
        setEntries(prev => [...prev, ...transformedData]);
        
        // Update balances for the new entries
        await fetchAndUpdateBalances(transformedData);
        
        console.log(`Added ${transformedData.length} entries for selected borrower`);
      } else {
        console.log('No collection entries found for this borrower');
      }
    } catch (err) {
      console.error('Error in fetchBorrowerCollectionEntry:', err);
    } finally {
      setIsFiltering(false);
    }
  }, [entries, transformRawData, fetchAndUpdateBalances]);

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .in('role', ['agent', 'superagent'])
        .order('username');

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    fetchCollectionEntries();
    fetchAgents();
  }, [fetchCollectionEntries]);

  // Monitor filter changes
  useEffect(() => {
    // Reset page when filters change
    setPage(0);
  }, [selectedBorrowerId, selectedAgent, selectedStatus]);

  const filteredEntries = entries.filter(entry => {
    const matchesBorrower = selectedBorrowerId ? entry.borrower.id === selectedBorrowerId : true;
    const matchesAgent = selectedAgent ? entry.assigned_agent?.username === selectedAgent : true;
    const matchesStatus = selectedStatus ? entry.status === selectedStatus : true;
    return matchesBorrower && matchesAgent && matchesStatus;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBorrowerSelect = useCallback(async (borrowerId: string | null) => {
    setSelectedBorrowerId(borrowerId);
    setIsFiltering(true);
    
    if (borrowerId) {
      // Fetch the borrower's collection entry if needed
      await fetchBorrowerCollectionEntry(borrowerId);
    }
    
    setTimeout(() => setIsFiltering(false), 500);
  }, [fetchBorrowerCollectionEntry]);

  const clearFilters = () => {
    setSelectedBorrowerId(null);
    setSelectedAgent('');
    setSelectedStatus('');
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'For Collection':
        return {
          bg: '#ffebee',
          color: '#d32f2f'
        };
      case 'For CI':
        return {
          bg: '#e3f2fd',
          color: '#1976d2'
        };
      default:
        return {
          bg: '#f5f5f5',
          color: '#757575'
        };
    }
  };

  const getAgentColor = (agentName: string): string => {
    // Direct mapping of agents to specific colors
    const agentColors: { [key: string]: string } = {
      'Cleto': '#FFB74D',      // Orange
      'Fernan': '#4FC3F7',     // Blue
      'Fredo': '#81C784',      // Green
      'June Sheen': '#FF8A65', // Coral
      'Kelvin': '#9575CD',     // Purple
      'Noel': '#4DB6AC',       // Teal
      'SammieCRM': '#F06292',  // Pink
      // Add fallback colors for any new agents
      'default1': '#FFD54F',   // Amber
      'default2': '#7986CB',   // Indigo
      'default3': '#A1887F'    // Brown
    };

    return agentColors[agentName] || agentColors.default1;
  };

  const getAgentChipStyle = (agentName: string, forMobile = false) => {
    // Direct mapping of agents to specific colors
    const agentColors: { [key: string]: string } = {
      'Cleto': '#FFB74D',      // Orange
      'Fernan': '#4FC3F7',     // Blue
      'Fredo': '#81C784',      // Green
      'June Sheen': '#FF8A65', // Coral
      'Kelvin': '#9575CD',     // Purple
      'Noel': '#4DB6AC',       // Teal
      'SammieCRM': '#F06292',  // Pink
      // Add fallback colors for any new agents
      'default1': '#FFD54F',   // Amber
      'default2': '#7986CB',   // Indigo
      'default3': '#A1887F'    // Brown
    };

    const baseStyle = {
      backgroundColor: agentColors[agentName] || agentColors.default1,
      color: '#000000',
      borderRadius: '16px',
      padding: forMobile ? '1px 6px' : '4px 12px',
      fontSize: forMobile ? '0.7rem' : '0.875rem',
      fontWeight: 500,
      border: forMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
      boxShadow: forMobile ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
      minWidth: forMobile ? '50px' : '80px',
      textAlign: 'center' as const,
      opacity: 0.8 // Making colors slightly softer for better readability
    };

    return baseStyle;
  };

  const StatusChip = ({ status }: { status: string }) => (
    <Chip 
      label={status}
      size="small"
      sx={{
        bgcolor: status === 'For Collection' ? 'success.lighter' : 'warning.lighter',
        color: status === 'For Collection' ? 'success.dark' : 'warning.dark',
        fontWeight: 'medium',
        fontSize: isMobile ? '0.7rem' : '0.75rem',
        height: isMobile ? '22px' : '24px',
        '& .MuiChip-label': {
          px: isMobile ? 1 : 1.5
        }
      }}
    />
  );

  if (loading) {
    return (
      <Box sx={{ 
        py: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      py: isMobile ? 1 : 3, // Reduce top/bottom padding on mobile
      px: 0 // Remove horizontal padding
    }}>
      <Container 
        maxWidth={false}
        disableGutters={true} // Disable default container padding
        sx={{ 
          px: 0, // Remove all horizontal padding
          mx: 0, // Ensure no margin
          width: '100%'
        }}
      >
        {/* Title with adjusted padding */}
        <Typography 
          variant={isMobile ? "h5" : "h3"} 
          sx={{ 
            fontWeight: 800,
            mb: isMobile ? 1 : 3, // Reduce bottom margin on mobile
            background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            fontSize: isMobile ? '1.5rem' : '2.5rem',
            lineHeight: isMobile ? 1.2 : 1.3,
            px: isMobile ? 1 : 1, // Consistent horizontal padding
          }}
        >
          Collection Agency List
        </Typography>
        
        <Paper 
          elevation={0}
          sx={{ 
            p: isMobile ? 1 : 3,
            mx: 0, // Ensure zero horizontal margin
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: isMobile ? 1 : 2, // Reduced radius on mobile
            position: 'relative',
            width: '100%',
            boxSizing: 'border-box', // Include padding in width calculation
          }}
        >
          <Grid container spacing={isMobile ? 0.5 : 2} sx={{ mb: isMobile ? 1 : 3 }}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={entries}
                value={entries.find((entry) => entry.borrower.id === selectedBorrowerId) || null}
                getOptionLabel={(entry) => `${entry.borrower.name} (${entry.loandisk_id})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, entry) => handleBorrowerSelect(entry?.borrower.id || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={isMobile ? 'small' : 'medium'}
                    label="Search borrower"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="agent-filter-label">Filter by Agent</InputLabel>
                <Select
                  labelId="agent-filter-label"
                  value={selectedAgent}
                  label="Filter by Agent"
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: isMobile ? '0.875rem' : 'inherit'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>All Agents</em>
                  </MenuItem>
                  {agents.map((agent) => (
                    <MenuItem 
                      key={agent.id} 
                      value={agent.username}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Typography 
                        component="span" 
                        sx={{
                          backgroundColor: getAgentColor(agent.username),
                          color: '#000000',
                          borderRadius: '16px',
                          padding: isMobile ? '2px 8px' : '4px 12px',
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          fontWeight: 500,
                          opacity: 0.8
                        }}
                      >
                        {agent.username}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={selectedStatus}
                  label="Filter by Status"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: isMobile ? '0.875rem' : 'inherit'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>All Status</em>
                  </MenuItem>
                  {['For Collection', 'For CI'].map((status) => (
                    <MenuItem 
                      key={status} 
                      value={status}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <StatusChip status={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1}>
              <Button 
                fullWidth 
                variant="outlined"
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  height: isMobile ? '40px' : '100%',
                  fontSize: isMobile ? '0.8rem' : 'inherit'
                }}
                onClick={clearFilters}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Data count indicator */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              Showing {entries.length} of {totalCount} total records
              {filteredEntries.length !== entries.length && ` (${filteredEntries.length} after filtering)`}
            </Typography>
          </Box>

          {isFiltering ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredEntries.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No records found matching your filter criteria
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer 
                sx={{ 
                  width: '100%',
                  overflow: 'auto',
                  px: 0, // Remove horizontal padding
                  '&::-webkit-scrollbar': {
                    height: '4px', // Smaller scrollbar on mobile
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  }
                }}
              >
                <Table 
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    tableLayout: isMobile ? 'fixed' : 'auto',
                    width: '100%' // Ensure table takes full width
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <StyledTableCell sx={{ width: isMobile ? '55%' : 'auto' }}>
                        Borrower Name
                      </StyledTableCell>
                      {!isMobile && (
                        <>
                          <StyledTableCell>Contact</StyledTableCell>
                          <StyledTableCell>Address</StyledTableCell>
                          <StyledTableCell>Balance</StyledTableCell>
                          <StyledTableCell>Status</StyledTableCell>
                        </>
                      )}
                      <StyledTableCell sx={{ width: isMobile ? '45%' : 'auto' }}>
                        Agent
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredEntries
                    ).map((entry) => (
                      <TableRow 
                        key={entry.id}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                        onClick={() => navigate(entry.profile_path)}
                      >
                        <TableCell 
                          component="th" 
                          scope="row"
                          sx={{ 
                            ...(isMobile && {
                              paddingY: 0.75, // Reduce vertical padding
                              paddingX: 1, // Consistent horizontal padding
                              width: '55%',
                              maxWidth: 0,
                            })
                          }}
                        >
                          {isMobile ? (
                            <Box>
                              <Typography 
                                sx={{ 
                                  fontWeight: 700,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.85rem',
                                  maxWidth: '100%'
                                }}
                              >
                                {entry.borrower.name}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  display: 'block',
                                  mt: 0.25,
                                  fontWeight: 'medium',
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem'
                                }}
                              >
                                ₱{entry.account_balance.toLocaleString('en-PH', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontWeight: 700 }}>{entry.borrower.name}</Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell>{entry.borrower.mobile_no}</TableCell>
                            <TableCell>{entry.borrower.address}</TableCell>
                            <TableCell>
                              ₱{entry.account_balance.toLocaleString('en-PH', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={entry.status} />
                            </TableCell>
                          </>
                        )}
                        <TableCell 
                          sx={isMobile ? { 
                            py: 0.75, 
                            px: 1,
                            width: '45%',
                            maxWidth: 0
                          } : {}}
                        >
                          {entry.assigned_agent ? (
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{
                                backgroundColor: getAgentColor(entry.assigned_agent.username),
                                color: '#000000',
                                borderRadius: '16px',
                                padding: isMobile ? '3px 8px' : '4px 12px',
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                fontWeight: 500,
                                display: 'inline-block',
                                opacity: 0.8,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '100%' : 'auto'
                              }}
                            >
                              {entry.assigned_agent.username}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: isMobile ? '0.75rem' : 'inherit'
                              }}
                            >
                              Unassigned
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredEntries.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={isMobile ? [10, 25] : [25, 50, 100]}
                labelRowsPerPage={isMobile ? '' : 'Rows:'}
                sx={isMobile ? {
                  '.MuiTablePagination-toolbar': {
                    padding: '0px 4px',
                    minHeight: '48px',
                  },
                  '.MuiTablePagination-selectLabel': {
                    display: 'none',
                  },
                  '.MuiTablePagination-displayedRows': {
                    fontSize: '0.75rem',
                    margin: 0,
                  },
                  '.MuiTablePagination-actions': {
                    marginLeft: '4px',
                  },
                  '.MuiInputBase-root': {
                    marginRight: '4px',
                  }
                } : {}}
              />
              
              {/* Balance loading indicator */}
              {loadingBalances && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    p: 1,
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Updating balances...
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default CollectionAgencyList;
